<template>
  <div>
    <global-confirm-box ref="confirmBox" />

    <div class="d-flex flex-wrap align-center mb-4" style="gap: 8px 14px">
      <h2 class="pr-title">{{ $t("marketing") }}</h2>
      <v-btn-toggle v-model="activeTab" mandatory dense class="pr-seg">
        <v-btn v-for="tab in tabs" :key="tab.value" small>
          {{ tab.label }}
          <span class="pr-badge" :class="`pr-badge-${tab.tone}`">{{ tab.count }}</span>
        </v-btn>
      </v-btn-toggle>
      <span class="pr-sub">{{ $t("finance_marketing_hint") }}</span>
      <v-spacer />
      <primary-button v-if="isAdmin" @click="openAddDialog">
        <v-icon left small>mdi-plus</v-icon>
        {{ $t("finance_new_campaign") }}
      </primary-button>
    </div>

    <v-card outlined class="pr-table-card mb-4">
      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <v-simple-table>
        <thead>
          <tr>
            <th>{{ $t("finance_campaign") }}</th>
            <th>{{ $t("finance_period_label") }}</th>
            <th>{{ $t("finance_discount") }}</th>
            <th>{{ $t("finance_funding") }}</th>
            <th class="text-right">{{ $t("finance_order_count") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!filteredPromotions.length">
            <td colspan="5" class="text-center grey--text py-6">
              {{ loading ? $t("finance_loading") : $t("finance_no_campaigns_in_category") }}
            </td>
          </tr>
          <tr v-for="item in filteredPromotions" :key="item.id" class="pr-row" @click="openPromotion(item)">
            <td class="font-weight-bold">{{ item.name }}</td>
            <td>{{ formatDate(item.periodStart) }} - {{ formatDate(item.periodEnd) }}</td>
            <td>{{ item.discount }}</td>
            <td>
              <span class="pr-chip">{{ item.fundedBy ? $t(PROMOTION_FUNDING_META[item.fundedBy]) : "—" }}</span>
            </td>
            <td class="text-right font-weight-bold">{{ item.orderCount }}</td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-card>

    <div class="pr-note">
      <v-icon x-small class="mr-2">mdi-information-outline</v-icon>
      {{ $t("finance_promotions_note") }}
    </div>

    <promotion-detail-dialog v-model="detailOpen" :promotion="selectedPromotion" />

    <promotion-form-dialog
      v-if="isAdmin"
      v-model="dialogOpen"
      :saving="saving"
      @save="onSavePromotion"
    />
  </div>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import hasSnackbar from "@/mixins/hasSnackbar";
import PromotionDetailDialog from "./dialogs/PromotionDetailDialog.vue";
import PromotionFormDialog from "./dialogs/PromotionFormDialog.vue";
import { PROMOTION_FUNDING_META, formatDate } from "./constants";

const mapPromotionDto = (dto) => ({
  id: dto.id,
  name: dto.name,
  periodStart: dto.period_start,
  periodEnd: dto.period_end,
  status: dto.status,
  discount: dto.discount,
  fundedBy: dto.funded_by,
  orderaShare: dto.ordera_share,
  restaurantShare: dto.restaurant_share,
  orderCount: dto.order_count,
  newSales: dto.new_sales,
});

export default {
  name: "FinanceTabPromotions",

  components: { PromotionDetailDialog, PromotionFormDialog },

  mixins: [hasSnackbar],

  data() {
    return {
      PROMOTION_FUNDING_META,
      promotions: [],
      loading: false,
      saving: false,
      activeTab: 0,
      detailOpen: false,
      selectedPromotion: null,
      dialogOpen: false,
    };
  },

  computed: {
    restaurantId() {
      return this.$store.state.restaurant.id;
    },
    isAdmin() {
      return this.$store.state.user?.user?.type === "admin";
    },
    headers() {
      return [
        { text: this.$t("finance_campaign"), value: "name" },
        { text: this.$t("finance_period_label"), value: "period", sortable: false },
        { text: this.$t("finance_discount"), value: "discount" },
        { text: this.$t("finance_funding"), value: "fundedBy" },
        { text: this.$t("finance_order_count"), value: "orderCount", align: "end" },
      ];
    },
    tabs() {
      const statuses = [
        { value: "active", label: this.$t("finance_tab_active"), tone: "green" },
        { value: "completed", label: this.$t("finance_tab_completed"), tone: "gray" },
        { value: "planned", label: this.$t("finance_tab_planned"), tone: "amber" },
      ];
      return statuses.map((s) => ({
        ...s,
        count: this.promotions.filter((p) => p.status === s.value).length,
      }));
    },
    filteredPromotions() {
      const status = this.tabs[this.activeTab] ? this.tabs[this.activeTab].value : "active";
      return this.promotions.filter((p) => p.status === status);
    },
  },

  created() {
    this.refresh();
  },

  methods: {
    formatDate,

    async refresh() {
      if (!this.restaurantId) return;
      this.loading = true;
      try {
        const { data } = await restaurantFinanceService.getPromotions(this.restaurantId);
        this.promotions = (data?.data || []).map(mapPromotionDto);
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_promotions_load_error"));
      } finally {
        this.loading = false;
      }
    },

    openPromotion(promotion) {
      this.selectedPromotion = promotion;
      this.detailOpen = true;
    },

    openAddDialog() {
      this.dialogOpen = true;
    },

    async onSavePromotion(payload) {
      this.saving = true;
      try {
        const { data } = await restaurantFinanceService.createPromotion(
          this.restaurantId,
          payload
        );
        const created = mapPromotionDto(data.data || data);
        this.promotions = [created, ...this.promotions];
        this.showSuccessSnackbar(this.$t("finance_campaign_created"));
        this.dialogOpen = false;
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_campaign_create_error"));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.pr-title {
  font-size: 1.6rem;
  font-weight: 800;
  margin: 0;
}

.pr-sub {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
}

.pr-seg {
  border: 1px solid #d9dbe0;
  border-radius: 6px;
  background: #fff;
}

.pr-seg,
.pr-seg .v-btn {
  height: 48px !important;
}

.pr-seg .v-btn {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 700;
  background: transparent !important;
}

.pr-seg .v-btn--active {
  background: #1d1d1f !important;
  color: #fff !important;
}

.pr-badge {
  margin-left: 8px;
  border-radius: 999px;
  padding: 0 7px;
  font-size: 0.72rem;
}

.pr-badge-green {
  background: #e2f3e8;
  color: #1b6e3a;
}

.pr-badge-amber {
  background: #fdf0d5;
  color: #8a5a00;
}

.pr-badge-gray {
  background: #eeeeec;
  color: rgba(0, 0, 0, 0.7);
}

.pr-table-card ::v-deep th {
  background: #f6f6f4;
  font-size: 0.78rem !important;
  font-weight: 700 !important;
  color: rgba(0, 0, 0, 0.6) !important;
  height: 46px !important;
}

.pr-table-card ::v-deep td {
  font-size: 0.92rem !important;
  height: 58px !important;
}

.pr-row {
  cursor: pointer;
}

.pr-chip {
  border-radius: 6px;
  padding: 2px 10px;
  font-size: 0.8rem;
  background: #f1f1ef;
}

.pr-note {
  display: flex;
  align-items: flex-start;
  background: #f1f1ef;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.65);
}
</style>
